import type { CompletedPartDto, StartMultipartUploadResponseDto } from "~/shared/types/chats";

export type ProgressFn = (loaded: number, total: number) => void;

interface PutWithProgressOptions {
    mimeType?: string;
    cacheControl?: string;
    onProgress?: ProgressFn;
}

interface PutWithProgressResult {
    etag: string | null;
}

// XHR PUT — единственный способ получить upload-progress (fetch его не
// даёт). Используется и для single-PUT, и для каждой части multipart.
export function putWithProgress(
    url: string,
    body: Blob | File,
    options: PutWithProgressOptions = {},
): Promise<PutWithProgressResult> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url);
        if (options.mimeType) xhr.setRequestHeader("Content-Type", options.mimeType);
        if (options.cacheControl) xhr.setRequestHeader("Cache-Control", options.cacheControl);
        xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) return;
            options.onProgress?.(event.loaded, event.total);
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve({ etag: xhr.getResponseHeader("ETag") });
            } else {
                reject(new Error(`S3 PUT failed: ${xhr.status}`));
            }
        };
        xhr.onerror = () => reject(new Error("Network error during S3 PUT"));
        xhr.onabort = () => reject(new Error("Upload aborted"));
        xhr.send(body);
    });
}

// Multipart upload с агрегатным прогрессом по всем частям. Размер
// каждой части — `chunkSizeBytes` от сервера (последняя короче).
// Concurrency — `maxConcurrency` параллельных PUT'ов. ETag собираем
// из заголовка ответа на каждую часть, complete вызывается снаружи
// (см. useUploadAttachment).
export async function uploadFileMultipartWithProgress(
    file: File,
    startResponse: StartMultipartUploadResponseDto,
    onPercent: (percent: number) => void,
): Promise<CompletedPartDto[]> {
    const { parts, chunkSizeBytes, maxConcurrency } = startResponse;
    const total = file.size;
    // loaded-bytes per partNumber — сумма всех ключей даёт total uploaded.
    const partLoaded = new Map<number, number>();

    const reportTotal = () => {
        let loaded = 0;
        for (const v of partLoaded.values()) loaded += v;
        const pct = total > 0 ? Math.floor((loaded / total) * 100) : 0;
        onPercent(Math.min(pct, 100));
    };

    const uploadOne = async (part: { partNumber: number; uploadUrl: string }): Promise<CompletedPartDto> => {
        const start = (part.partNumber - 1) * chunkSizeBytes;
        const end = Math.min(start + chunkSizeBytes, file.size);
        const chunk = file.slice(start, end);
        const { etag } = await putWithProgress(part.uploadUrl, chunk, {
            onProgress: (loaded) => {
                partLoaded.set(part.partNumber, loaded);
                reportTotal();
            },
        });
        if (!etag) throw new Error(`No ETag in response for part ${part.partNumber}`);
        partLoaded.set(part.partNumber, end - start);
        reportTotal();
        return { partNumber: part.partNumber, etag };
    };

    const completed: CompletedPartDto[] = [];
    // Concurrency — батч по maxConcurrency. Между батчами — await Promise.all.
    for (let i = 0; i < parts.length; i += maxConcurrency) {
        const batch = parts.slice(i, i + maxConcurrency);
        const results = await Promise.all(batch.map(uploadOne));
        completed.push(...results);
    }
    // Сортируем по partNumber: complete-endpoint всё равно сортирует, но
    // отдаём детерминированный порядок чтобы тесты были стабильны.
    return completed.sort((a, b) => a.partNumber - b.partNumber);
}
